import {
  isRecord,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization/primitives";

export type CompareTimeseriesAlignmentMode = "date_join" | "index";

type CompareConfigArgs = {
  output: Record<string, unknown>;
  includeContext?: boolean;
};

type IndexedCompareTimeseriesRow = {
  date: string;
  comparingDate: string | null;
  currentRow: Record<string, unknown>;
  comparingRow: Record<string, unknown> | null;
};

function resolveCompareConfigSources(args: CompareConfigArgs): {
  context: Record<string, unknown> | null;
  applied: Record<string, unknown> | null;
} {
  return {
    context:
      args.includeContext && isRecord(args.output.context)
        ? args.output.context
        : null,
    applied: isRecord(args.output.applied) ? args.output.applied : null,
  };
}

/**
 * compare dataset の整列方式を tool output から解決する。
 *
 * source に値がない場合は既定の `date_join` を返し、比較軸を安定化する。
 */
export function resolveCompareAlignmentMode(
  args: CompareConfigArgs,
): CompareTimeseriesAlignmentMode {
  const sources = resolveCompareConfigSources(args);
  const raw = toString(
    args.output.compareAlignmentMode ??
      sources.context?.compareAlignmentMode ??
      sources.applied?.compareAlignmentMode,
  );
  return raw === "index" ? "index" : "date_join";
}

/**
 * compare 用 date range（current/previous）を tool output から取り出す。
 */
export function resolveCompareDateRanges(args: CompareConfigArgs): {
  currentDateRange: Record<string, unknown> | null;
  comparingDateRange: Record<string, unknown> | null;
} {
  const sources = resolveCompareConfigSources(args);
  const currentRangeCandidate =
    args.output.dateRange ??
    sources.context?.dateRange ??
    sources.applied?.dateRange;
  const comparingRangeCandidate =
    args.output.comparingDateRange ??
    sources.context?.comparingDateRange ??
    sources.applied?.comparingDateRange;
  const currentDateRange = isRecord(currentRangeCandidate)
    ? currentRangeCandidate
    : null;
  const comparingDateRange = isRecord(comparingRangeCandidate)
    ? comparingRangeCandidate
    : null;
  return { currentDateRange, comparingDateRange };
}

function normalizeDateSortKey(value: string): string {
  const trimmed = value.trim();
  const ymdMatch = /^\d{4}-\d{2}-\d{2}/.exec(trimmed);
  if (ymdMatch?.[0]) return ymdMatch[0];
  return trimmed;
}

function mapRecordItems<T>(
  items: unknown[],
  mapper: (item: Record<string, unknown>) => T | null,
): T[] {
  const mapped: T[] = [];
  for (const item of items) {
    if (!isRecord(item)) continue;
    const value = mapper(item);
    if (!value) continue;
    mapped.push(value);
  }
  return mapped;
}

function toDatedRows(
  items: unknown[],
  dateKey: string,
): Array<{ date: string; row: Record<string, unknown> }> {
  const rows = mapRecordItems(items, (item) => {
    const date = toString(item[dateKey]);
    if (!date) return null;
    return { date, row: item };
  });
  return rows.sort((a, b) =>
    normalizeDateSortKey(a.date).localeCompare(normalizeDateSortKey(b.date)),
  );
}

function toDateKey(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ymdMatch = /^\d{4}-\d{2}-\d{2}/.exec(trimmed);
  if (ymdMatch?.[0]) return ymdMatch[0];
  return null;
}

function toUtcDate(value: string): Date | null {
  const key = toDateKey(value);
  if (!key) return null;
  const parsed = new Date(`${key}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function diffDays(current: Date, previous: Date): number {
  return Math.round((current.getTime() - previous.getTime()) / 86400000);
}

function shiftDateKey(value: string, dayDelta: number): string | null {
  const base = toUtcDate(value);
  if (!base) return null;
  base.setUTCDate(base.getUTCDate() + dayDelta);
  return base.toISOString().slice(0, 10);
}

function inferDateShiftDaysFromRanges(args: {
  currentDateRange?: unknown;
  comparingDateRange?: unknown;
}): number | null {
  const currentRange = isRecord(args.currentDateRange)
    ? args.currentDateRange
    : null;
  const comparingRange = isRecord(args.comparingDateRange)
    ? args.comparingDateRange
    : null;
  if (!currentRange || !comparingRange) return null;

  const candidates: number[] = [];
  const pairedEdges: Array<[unknown, unknown]> = [
    [currentRange.start, comparingRange.start],
    [currentRange.end, comparingRange.end],
  ];
  for (const [currentValue, comparingValue] of pairedEdges) {
    const currentDate = toUtcDate(toString(currentValue) ?? "");
    const comparingDate = toUtcDate(toString(comparingValue) ?? "");
    if (!currentDate || !comparingDate) continue;
    candidates.push(diffDays(currentDate, comparingDate));
  }
  return candidates[0] ?? null;
}

function inferDateShiftDaysFromRows(args: {
  currentRows: Array<{ date: string; row: Record<string, unknown> }>;
  comparingRows: Array<{ date: string; row: Record<string, unknown> }>;
}): number | null {
  const pairCount = Math.min(
    args.currentRows.length,
    args.comparingRows.length,
  );
  if (pairCount <= 0) return null;

  const counts = new Map<number, number>();
  for (let index = 0; index < pairCount; index += 1) {
    const currentDate = toUtcDate(args.currentRows[index]?.date ?? "");
    const comparingDate = toUtcDate(args.comparingRows[index]?.date ?? "");
    if (!currentDate || !comparingDate) continue;
    const delta = diffDays(currentDate, comparingDate);
    counts.set(delta, (counts.get(delta) ?? 0) + 1);
  }

  const ranked = [...counts.entries()].sort((left, right) => {
    if (right[1] !== left[1]) return right[1] - left[1];
    return Math.abs(left[0]) - Math.abs(right[0]);
  });
  return ranked[0]?.[0] ?? null;
}

function buildComparingBuckets(
  rows: Array<{ date: string; row: Record<string, unknown> }>,
): Map<string, Array<{ date: string; row: Record<string, unknown> }>> {
  const buckets = new Map<
    string,
    Array<{ date: string; row: Record<string, unknown> }>
  >();
  for (const row of rows) {
    const key = toDateKey(row.date);
    if (!key) continue;
    const list = buckets.get(key) ?? [];
    list.push(row);
    buckets.set(key, list);
  }
  return buckets;
}

function takeBucketRow(
  buckets: Map<string, Array<{ date: string; row: Record<string, unknown> }>>,
  key: string,
): { date: string; row: Record<string, unknown> } | null {
  const list = buckets.get(key);
  if (!list || list.length === 0) return null;
  const next = list.shift() ?? null;
  if (list.length === 0) {
    buckets.delete(key);
  }
  return next;
}

function alignCompareTimeseriesByIndexRows(args: {
  currentRows: Array<{ date: string; row: Record<string, unknown> }>;
  comparingRows: Array<{ date: string; row: Record<string, unknown> }>;
}): IndexedCompareTimeseriesRow[] {
  return args.currentRows.map((current, index) => {
    const comparing = args.comparingRows[index];
    return {
      date: current.date,
      comparingDate: comparing?.date ?? null,
      currentRow: current.row,
      comparingRow: comparing?.row ?? null,
    };
  });
}

function alignCompareTimeseriesByDateJoinRows(args: {
  currentRows: Array<{ date: string; row: Record<string, unknown> }>;
  comparingRows: Array<{ date: string; row: Record<string, unknown> }>;
  dateShiftDays: number | null;
}): IndexedCompareTimeseriesRow[] {
  const comparingBuckets = buildComparingBuckets(args.comparingRows);

  return args.currentRows.map((current) => {
    const currentDateKey = toDateKey(current.date);
    const candidateKeys: string[] = [];
    if (currentDateKey && args.dateShiftDays !== null) {
      const shifted = shiftDateKey(currentDateKey, -args.dateShiftDays);
      if (shifted) candidateKeys.push(shifted);
    }
    if (currentDateKey && args.dateShiftDays === null) {
      candidateKeys.push(currentDateKey);
    }

    let matched: { date: string; row: Record<string, unknown> } | null = null;
    for (const key of candidateKeys) {
      matched = takeBucketRow(comparingBuckets, key);
      if (matched) break;
    }

    return {
      date: current.date,
      comparingDate: matched?.date ?? null,
      currentRow: current.row,
      comparingRow: matched?.row ?? null,
    };
  });
}

/**
 * current/comparing の timeseries を整列させる。
 *
 * - `date_join`（既定）: 期間シフト日数を推定し、日付対応で結合する。
 * - `index`: ソート後の相対インデックスで対応付ける。
 */
export function alignCompareTimeseries(args: {
  currentItems: unknown[];
  comparingItems: unknown[];
  dateKey?: string;
  alignmentMode?: CompareTimeseriesAlignmentMode;
  currentDateRange?: unknown;
  comparingDateRange?: unknown;
  dateShiftDays?: number;
}): IndexedCompareTimeseriesRow[] {
  const dateKey = args.dateKey ?? "date";
  const currentRows = toDatedRows(args.currentItems, dateKey);
  const comparingRows = toDatedRows(args.comparingItems, dateKey);
  const alignmentMode = args.alignmentMode ?? "date_join";

  if (alignmentMode === "index") {
    return alignCompareTimeseriesByIndexRows({
      currentRows,
      comparingRows,
    });
  }

  const dateShiftDays =
    typeof args.dateShiftDays === "number" &&
    Number.isFinite(args.dateShiftDays)
      ? Math.round(args.dateShiftDays)
      : (inferDateShiftDaysFromRanges({
          currentDateRange: args.currentDateRange,
          comparingDateRange: args.comparingDateRange,
        }) ??
        inferDateShiftDaysFromRows({
          currentRows,
          comparingRows,
        }));

  return alignCompareTimeseriesByDateJoinRows({
    currentRows,
    comparingRows,
    dateShiftDays,
  });
}

/**
 * 明示的に index 整列を使いたい場合に利用する。
 */
export function alignCompareTimeseriesByIndex(args: {
  currentItems: unknown[];
  comparingItems: unknown[];
  dateKey?: string;
}): IndexedCompareTimeseriesRow[] {
  return alignCompareTimeseries({ ...args, alignmentMode: "index" });
}
